import { BoxProps } from "@mui/material/Box";

import CustomBasePage from "../CustomBasePage";
import {
  bootItems,
  ICartItem,
} from "@xyo-network/api-checkout-apollo/bindings/src/utils/items";
import { rebillyType } from "../components/Boots/api";

import { CheckoutV3Mutation } from "@xyo-network/api-checkout-apollo";
import {
  CheckoutProps,
  CheckoutProviders,
  CheckoutBaseCss,
  CheckoutPageWrapper,
  OrderSummary,
  Payment,
  useEmail,
  usePayment,
} from "@xyo-network/api-checkout-apollo/bindings/src/components/Checkout";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { Wrapper } from "../components/Layouts";
import { proPlanFeatures } from "../components/Plans/ProPlanCard";
import { plusPlanFeatures } from "../components/Plans/PlusPlanCard";
import { litePlanFeatures } from "../components/Plans/LitePlanCard";
import { useEffect, useState } from "react";
import * as mixpanel from "mixpanel-browser";
import { useUser, useUserInfo } from "../User";
import {
  Dialog,
  Typography,
  DialogContent,
  DialogTitle,
  Button,
  DialogActions,
  Box,
  Alert,
  Divider,
} from "@mui/material";
import useMySubscription from "../Subscriptions/useMySubscription";
import { checkoutClient } from "../lib";

const plans: Record<
  string,
  ICartItem & { requiredToggles?: { id: string; label: string }[] }
> = {
  pro: {
    planId: "XYO-COIN-PRO-3D-TRIAL-34-95",
    name: "Pro Subscription",
    image: "",
    quantity: 1,
    amount: 0,
    subtitle:
      "<p>Free trial for 3 days. Then $34.95/month. Cancel anytime.</p>", //toList(proPlanFeatures),
    requiredToggles: [
      {
        id: "free-trial",
        label:
          "I understand that I will be charged $0 for the first 3 days, and then $34.95/month after that. I can cancel anytime.",
      },
    ],
  },
  plus: {
    planId: "XYO-COIN-PLUS-3D-TRIAL-24-95",
    name: "Plus Subscription",
    image: "",
    quantity: 1,
    amount: 0,
    subtitle:
      "<p>Free trial for 3 days. Then $24.95/month. Cancel anytime.</p>", //toList(plusPlanFeatures),
    requiredToggles: [
      {
        id: "free-trial",
        label:
          "I understand that I will be charged $0 for the first 3 days, and then $24.95/month after that. I can cancel anytime.",
      },
    ],
  },
  lite: {
    planId: "XYO-COIN-LITE-3D-TRIAL-7-95",
    name: "Lite Subscription",
    image: "",
    quantity: 1,
    amount: 0,
    subtitle: "<p>Free trial for 3 days. Then $7.95/month. Cancel anytime.</p>", //toList(litePlanFeatures),
    requiredToggles: [
      {
        id: "free-trial",
        label:
          "I understand that I will be charged $0 for the first 3 days, and then $7.95/month after that. I can cancel anytime.",
      },
    ],
  },
};

export const Checkout = ({ hideHeader, ...props }: CheckoutProps) => {
  return (
    <CheckoutProviders {...props}>
      <CheckoutBaseCss {...props}>
        <CheckoutPageWrapper>
          <OrderSummary hideHeader={hideHeader} />
          <Payment />
          <ConnectedEmail />
        </CheckoutPageWrapper>
      </CheckoutBaseCss>
    </CheckoutProviders>
  );
};

const ConnectedEmail = () => {
  const { user } = useUser();
  const { findField } = usePayment();

  useEffect(() => {
    if (user?.email) {
      const field = findField("email");
      if (field) {
        field.value = user.email;
      }
    }
  }, [user]);

  return null;
};

const Subscribe: React.FC<React.PropsWithChildren<BoxProps>> = (props) => {
  const { plan } = useParams();
  const navigate = useNavigate();
  const { userInfo, activeRebillySubscription } = useMySubscription();
  const [result, setResult] = useState<
    | { type: "error"; error?: Error }
    | { type: "success"; purchase: CheckoutV3Mutation["checkout"] }
    | null
  >(null);

  const item = plans[plan as keyof typeof plans];
  const itemTracker = {};

  useEffect(() => {
    try {
      if (userInfo && userInfo.ownerUID) {
        mixpanel.identify(userInfo.ownerUID);
      }
      if (userInfo && itemTracker) {
        mixpanel.track("SubscribeViewed", { ...itemTracker });
      }
    } catch (err) {
      console.log("Identify error", err);
    }
  }, [userInfo, itemTracker]);

  if (!item) {
    return <Navigate to="/" />;
  }

  const handlePurchaseCompleted = (
    purchase: CheckoutV3Mutation["checkout"],
  ) => {
    console.log(purchase);
    setResult({ type: "success", purchase });
    mixpanel.track("SubscribeCompleted", {
      ...itemTracker,
    });
    checkoutClient.resetStore().catch((error) => {
      console.error("Error resetting checkout store:", error);
    });
  };

  const handlePurchaseFailed = (error: Error) => {
    console.error(error);
    setResult({ type: "error", error });
    mixpanel.track("SubscribeFailed", {
      ...itemTracker,
      error: error.message,
    });
  };

  return (
    <Wrapper style={{ flex: 1 }}>
      {activeRebillySubscription && (
        <div style={{ maxWidth: 900, margin: "12px auto" }}>
          <Alert severity="info" sx={{ mx: "auto" }}>
            <Typography variant="body2">
              You already have an active subscription.
            </Typography>
          </Alert>
        </div>
      )}
      <Checkout
        darkMode
        hideHeader
        businessName="COIN"
        businessLogo="https://my.coinapp.co/logo.svg"
        termsLink="https://coinapp.co/legal/terms"
        privacyLink="https://coinapp.co/legal/privacy"
        submitText="Subscribe"
        submittingText="Subscribing..."
        requiredToggles={[
          {
            id: "read-terms",
            label: (
              <>
                I have read and agree to the{" "}
                <a
                  href="https://coinapp.co/legal/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Terms of Service
                </a>
              </>
            ),
          },
          ...(item.requiredToggles || []),
        ]}
        // framePayEnabled
        rebillyType={rebillyType}
        items={[item]}
        onContactInfoCompleted={() => {
          mixpanel.track("ContactInfoCompleted", {
            ...itemTracker,
          });
        }}
        onBillingInfoCompleted={() => {
          mixpanel.track("BillingInfoCompleted", {
            ...itemTracker,
          });
        }}
        onPaymentInfoCompleted={() => {
          mixpanel.track("PaymentInfoCompleted", {
            ...itemTracker,
          });
        }}
        onPurchaseAttempted={() => {
          mixpanel.track("PurchaseAttempted", {
            ...itemTracker,
          });
        }}
        onPurchaseCompleted={handlePurchaseCompleted}
        onPurchaseFailed={handlePurchaseFailed}
      />

      <Dialog
        open={result?.type === "success"}
        onClose={() => navigate("/")}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: "center", color: "success.main" }}>
          🎉 Subscription Activated!
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: "center", mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Welcome to {item.name}!
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              Your subscription has been successfully activated and you now have
              access to all premium features.
            </Typography>
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Free Trial Details:</strong>
              <br />
              • Your 3-day free trial starts now
              <br />• You'll be charged{" "}
              {item.planId.includes("PRO")
                ? "$34.95"
                : item.planId.includes("PLUS")
                ? "$24.95"
                : "$7.95"}
              /month after the trial
              <br />• Cancel anytime from your account settings
            </Typography>
          </Alert>

          {result?.type === "success" && result.purchase?.transaction?.id && (
            <Box sx={{ mt: 2, p: 2, bgcolor: "grey", borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Transaction ID: {result.purchase.transaction.id}
              </Typography>
            </Box>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Start exploring your new features right away! Check out the enhanced
            earning opportunities available in your account.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button
            variant="contained"
            onClick={() => navigate("/")}
            size="large"
          >
            Start Earning
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={result?.type === "error"}
        onClose={() => setResult(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: "error.main" }}>
          ⚠️ Subscription Failed
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            We couldn't process your subscription to {item.name}. Don't worry,
            you haven't been charged.
          </Typography>

          {result?.type === "error" && result.error && (
            <Alert severity="error" sx={{ my: 2 }}>
              <Typography variant="body2">
                <strong>Error Details:</strong>
                <br />
                {result.error.message ||
                  "An unexpected error occurred during payment processing."}
              </Typography>
            </Alert>
          )}

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Common solutions:
            </Typography>
            <Typography variant="body2" component="div">
              • Check that your payment information is correct
              <br />
              • Ensure your card has sufficient funds
              <br />
              • Try a different payment method
              <br />• Contact your bank if the issue persists
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="body2" color="text.secondary">
            Need help? Contact our support team at{" "}
            <strong>support@coinapp.co</strong> or through the help center in
            your account.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 3 }}>
          <Button onClick={() => setResult(null)}>Try Again</Button>
          <Button variant="outlined" onClick={() => navigate("/")}>
            Back to Home
          </Button>
        </DialogActions>
      </Dialog>
    </Wrapper>
  );
};

function toList(items: string[]) {
  return `<ul>${items.map((item) => `<li>${item}</li>`).join("\n")}</ul>`;
}

export default Subscribe;
